import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { ReactNode } from 'react'
import { REALTIME_SUBSCRIBE_STATES } from '@supabase/supabase-js'
import type { RealtimeChannel } from '@supabase/supabase-js'

import { initLogger } from '@/lib/logger.ts'
import { supabaseClient } from '@/clients/supabase-client.ts'
import { useAuth } from '@/features/auth/hooks/use-auth.ts'

const logger = initLogger('supabase-realtime-context')

interface SupabaseRealtimeContextValue {
  state: REALTIME_SUBSCRIBE_STATES | null
  subscribe: (onTableChange: (tableName: string) => void) => () => void
}

const SupabaseRealtimeContext =
  createContext<SupabaseRealtimeContextValue | null>(null)

interface SupabaseRealtimeProviderProps {
  children: ReactNode
}

export function SupabaseRealtimeProvider({
  children,
}: SupabaseRealtimeProviderProps) {
  const auth = useAuth()
  const [state, setState] = useState<REALTIME_SUBSCRIBE_STATES | null>(null)

  // Global channel and reference counting
  const channelRef = useRef<RealtimeChannel | null>(null)
  const refCountRef = useRef(0)
  const subscribersRef = useRef<Set<(tableName: string) => void>>(new Set())

  // Subscribe function that manages reference counting
  const subscribe = useMemo(() => {
    return (onTableChange: (tableName: string) => void) => {
      if (!auth.user?.id) {
        logger.warn('Cannot subscribe to realtime: user not authenticated')
        return () => {} // noop unsubscribe
      }

      // Add subscriber to the set
      subscribersRef.current.add(onTableChange)
      refCountRef.current += 1

      logger.info(
        'Realtime subscription requested, refCount:',
        refCountRef.current
      )

      // Create channel if it doesn't exist
      if (!channelRef.current) {
        const channelName = `schema-db-changes-${auth.user.id}`

        channelRef.current = supabaseClient.channel(channelName).on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
          },
          (payload) => {
            logger.debug({ payload }, 'Detected database change')
            // Notify all subscribers
            subscribersRef.current.forEach((callback) => {
              callback(payload.table)
            })
          }
        )

        channelRef.current.subscribe((subscriptionState) => {
          logger.info('Realtime subscription state changed:', subscriptionState)
          setState(subscriptionState)
        })

        logger.info('Created new realtime channel')
      } else {
        logger.info('Reusing existing realtime channel')
      }

      // Return unsubscribe function
      return () => {
        subscribersRef.current.delete(onTableChange)
        refCountRef.current -= 1

        logger.info(
          'Realtime unsubscribe requested, refCount:',
          refCountRef.current
        )

        // Clean up channel when no more subscribers
        if (refCountRef.current <= 0 && channelRef.current) {
          logger.info('Cleaning up realtime channel (no more subscribers)')
          channelRef.current.unsubscribe()
          channelRef.current = null
          refCountRef.current = 0
          subscribersRef.current.clear()
          setState(null)
        }
      }
    }
  }, [auth.user?.id])

  // Clean up on user logout
  useEffect(() => {
    if (!auth.user?.id && channelRef.current) {
      logger.info('User logged out - cleaning up realtime channel')
      channelRef.current.unsubscribe()
      channelRef.current = null
      refCountRef.current = 0
      subscribersRef.current.clear()
      setState(null)
    }
  }, [auth.user?.id])

  const value = useMemo(
    () => ({
      state,
      subscribe,
    }),
    [state, subscribe]
  )

  return (
    <SupabaseRealtimeContext.Provider value={value}>
      {children}
    </SupabaseRealtimeContext.Provider>
  )
}

export function useSupabaseRealtime(): SupabaseRealtimeContextValue {
  const context = useContext(SupabaseRealtimeContext)
  if (!context) {
    throw new Error(
      'useSupabaseRealtime must be used within SupabaseRealtimeProvider'
    )
  }
  return context
}
